import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Loader2, Search, AlertCircle, DollarSign, Users, TrendingDown, RefreshCw, Plus, CheckCircle2, XCircle, Edit2, Trash2, ChevronsUpDown, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import AgentListPanel from '@/components/portal/AgentListPanel';

export default function DebtMonitoring({ userInfo }) {
  const [debts, setDebts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [selectedAgentId, setSelectedAgentId] = useState(null);
  
  // Agent table filters
  const [agentSearchQuery, setAgentSearchQuery] = useState('');
  const [selectedAgentFilters, setSelectedAgentFilters] = useState([]);
  const [agentFilterOpen, setAgentFilterOpen] = useState(false);
  
  // Debt records filters
  const [selectedCarrierFilters, setSelectedCarrierFilters] = useState([]);
  const [carrierFilterOpen, setCarrierFilterOpen] = useState(false);
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [selectedStatusFilters, setSelectedStatusFilters] = useState([]);
  const [statusFilterOpen, setStatusFilterOpen] = useState(false);
  
  // Edit debt modal
  const [editingDebt, setEditingDebt] = useState(null);
  const [showEditDebtModal, setShowEditDebtModal] = useState(false);
  const [editFormData, setEditFormData] = useState({
    carrier_id: '',
    amount: '',
    isResolved: false
  });
  const [editCarrierSearch, setEditCarrierSearch] = useState('');
  
  // Confirmation modals
  const [showEditConfirmModal, setShowEditConfirmModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [deletingDebt, setDeletingDebt] = useState(null);
  
  // Email modal
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [agentEmail, setAgentEmail] = useState('');

  // Create Report Modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState({
    npn: '',
    carrier_id: '',
    amount: ''
  });
  const [carriers, setCarriers] = useState([]);
  const [carriersCached, setCarriersCached] = useState(false); // Track if carriers are loaded
  const [carrierSearch, setCarrierSearch] = useState('');
  const [loadingCarriers, setLoadingCarriers] = useState(false);
  const [npnVerification, setNpnVerification] = useState({ status: '', data: null });
  const [verifyingNpn, setVerifyingNpn] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [reportsAdded, setReportsAdded] = useState([]); // Track reports added in this session
  
  // Confirmation Modal
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Edit State
  const [editingIndex, setEditingIndex] = useState(null); // Track which entry is being edited
  const [isEditMode, setIsEditMode] = useState(false); // Track if we're in edit mode

  // Fetch debts
  const fetchDebts = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get('https://api1.simplyworkcrm.com/api:JBYRWqH3/portal/internal/allDebt', {
        params: {
          user_id: userInfo?.user_id,
          location_id: userInfo?.location_id
        }
      });
      setDebts(response.data);
    } catch (err) {
      console.error('Error fetching debts:', err);
      setError('Failed to load debt data. Please try again.');
      toast.error('Failed to load debt data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userInfo?.user_id) {
      fetchDebts();
    }
  }, [userInfo]);

  // Fetch carriers for dropdown (only once)
  const fetchCarriers = async () => {
    // Return early if already cached
    if (carriersCached && carriers.length > 0) {
      return;
    }

    try {
      setLoadingCarriers(true);
      const response = await axios.get('https://api1.simplyworkcrm.com/api:JBYRWqH3/meta/pms/carrier');
      const carrierList = Array.isArray(response.data) 
        ? response.data.map(c => ({ value: c.id, label: c.name }))
        : [];
      setCarriers(carrierList);
      setCarriersCached(true); // Mark as cached
    } catch (err) {
      console.error('Error fetching carriers:', err);
      toast.error('Failed to load carriers');
    } finally {
      setLoadingCarriers(false);
    }
  };

  // Verify NPN
  const verifyNPN = async (npn) => {
    const npnStr = npn ? String(npn).trim() : '';
    
    if (!npnStr || npnStr === '') {
      setNpnVerification({ status: '', data: null });
      return;
    }

    try {
      setVerifyingNpn(true);
      setNpnVerification({ status: 'verifying', data: null });
      
      const response = await axios.get('https://api1.simplyworkcrm.com/api:JBYRWqH3/auth/verifyNPN', {
        params: { npn: npnStr }
      });

      if (response.data.status === 'active') {
        setNpnVerification({ 
          status: 'success', 
          data: response.data 
        });
        
        // Check if email exists, if not show email modal
        if (!response.data.email || response.data.email.trim() === '') {
          setShowEmailModal(true);
        } else {
          setAgentEmail(response.data.email);
        }
        
        toast.success(`NPN verified: ${response.data.first_name} ${response.data.last_name}`);
      } else {
        setNpnVerification({ status: 'inactive', data: response.data });
        toast.error('NPN status is not active');
      }
    } catch (err) {
      console.error('Error verifying NPN:', err);
      setNpnVerification({ status: 'error', data: null });
      toast.error('Failed to verify NPN. Please check the number and try again.');
    } finally {
      setVerifyingNpn(false);
    }
  };

  // Handle email submission
  const handleEmailSubmit = () => {
    if (!agentEmail || !agentEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    setShowEmailModal(false);
    toast.success('Email added successfully');
  };

  // Handle create report
  const handleOpenCreateModal = async () => {
    setFormData({
      npn: '',
      carrier_id: '',
      amount: ''
    });
    setNpnVerification({ status: '', data: null });
    setCarrierSearch('');
    setReportsAdded([]);
    setIsEditMode(false);
    setEditingIndex(null);
    setAgentEmail('');
    setShowCreateModal(true);
    await fetchCarriers(); // Will use cache if available
  };

  // Handle save button click - show confirmation
  const handleSaveClick = () => {
    if (reportsAdded.length > 0) {
      setShowConfirmModal(true);
    }
  };

  // Handle confirm save - submit to API
  const handleConfirmSave = async () => {
    try {
      setSaving(true);

      // Prepare the debtRecord array
      const debtRecord = reportsAdded.map(report => ({
        carrier_id: report.carrier_id,
        amount: report.amount
      }));

      const payload = {
        npn: parseInt(formData.npn),
        email: agentEmail,
        debtRecord: debtRecord
      };

      await axios.post(
        'https://api1.simplyworkcrm.com/api:JBYRWqH3/portal/internal/debtCreate',
        payload,
        {
          params: {
            user_id: userInfo?.user_id,
            location_id: userInfo?.location_id
          }
        }
      );

      toast.success(`Successfully created ${reportsAdded.length} debt ${reportsAdded.length === 1 ? 'entry' : 'entries'}`);
      setShowConfirmModal(false);
      setShowCreateModal(false);
      fetchDebts(); // Refresh the list
    } catch (err) {
      console.error('Error creating debt reports:', err);
      toast.error('Failed to create debt reports. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Handle closing modal
  const handleCloseModal = () => {
    if (reportsAdded.length > 0) {
      setShowConfirmModal(true);
    } else {
      setShowCreateModal(false);
    }
  };

  // Handle delete entry
  const handleDeleteEntry = (index) => {
    const deletedEntry = reportsAdded[index];
    setReportsAdded(prev => prev.filter((_, i) => i !== index));
    toast.success(`Removed ${deletedEntry.carrier_name} entry`);
  };

  // Handle edit entry
  const handleEditEntry = (index) => {
    const entryToEdit = reportsAdded[index];
    
    // Populate form with entry data
    setFormData(prev => ({
      ...prev,
      carrier_id: entryToEdit.carrier_id,
      amount: entryToEdit.amount.toString()
    }));
    
    // Remove the entry from the list
    setReportsAdded(prev => prev.filter((_, i) => i !== index));
    
    // Mark as edit mode
    setIsEditMode(true);
    setEditingIndex(index);
    
    toast.info(`Editing ${entryToEdit.carrier_name} entry`);
  };

  // Handle submit - Add report and keep modal open
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate NPN
    if (!formData.npn || npnVerification.status !== 'success') {
      toast.error('Please verify the Agent NPN before submitting');
      return;
    }

    // Validate carrier
    if (!formData.carrier_id) {
      toast.error('Please select a carrier');
      return;
    }

    // Validate amount
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    try {
      setSubmitting(true);
      
      const reportData = {
        npn: formData.npn,
        carrier_id: formData.carrier_id, // Store carrier_id for API
        amount: parseFloat(formData.amount),
        agent_name: `${npnVerification.data.first_name} ${npnVerification.data.last_name}`,
        carrier_name: carriers.find(c => c.value === formData.carrier_id)?.label || 'Unknown'
      };

      // Add to tracking list
      setReportsAdded(prev => [...prev, reportData]);
      
      if (isEditMode) {
        toast.success(`Updated ${reportData.carrier_name} entry`);
        setIsEditMode(false);
        setEditingIndex(null);
      } else {
        toast.success(`Debt report added for ${reportData.carrier_name}`);
      }
      
      // Reset only carrier and amount, keep NPN verified
      setFormData(prev => ({
        ...prev,
        carrier_id: '',
        amount: ''
      }));
      setCarrierSearch('');
    } catch (err) {
      console.error('Error creating debt report:', err);
      toast.error('Failed to create debt report. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Calculate agent summaries
  const agentSummaries = useMemo(() => {
    const agentMap = new Map();
    
    debts.forEach(debt => {
      const agentId = debt.agentOnDebt_id;
      if (!agentId) return;
      
      if (!agentMap.has(agentId)) {
        agentMap.set(agentId, {
          id: agentId,
          name: debt.agentOnDebt_name || 'Unknown',
          npn: debt.agentOnDebt_npn || 'N/A',
          email: debt.agentOnDebt_email || 'N/A',
          resolvedAmount: 0,
          unresolvedAmount: 0,
          totalDebts: 0
        });
      }
      
      const agent = agentMap.get(agentId);
      agent.totalDebts += 1;
      
      if (debt.isResolved) {
        agent.resolvedAmount += debt.amount || 0;
      } else {
        agent.unresolvedAmount += debt.amount || 0;
      }
    });
    
    return Array.from(agentMap.values());
  }, [debts]);

  // Get unique carriers for filter
  const uniqueCarriers = useMemo(() => {
    const carrierMap = new Map();
    debts.forEach(debt => {
      if (debt.carrier_id && debt.carrier_name) {
        carrierMap.set(debt.carrier_id, debt.carrier_name);
      }
    });
    return Array.from(carrierMap.entries()).map(([id, name]) => ({ id, name }));
  }, [debts]);

  // Filter debts
  const filteredDebts = useMemo(() => {
    let filtered = [...debts];

    // Filter by selected agent
    if (selectedAgentId) {
      filtered = filtered.filter(debt => debt.agentOnDebt_id === selectedAgentId);
    }

    // Filter by carriers
    if (selectedCarrierFilters.length > 0) {
      filtered = filtered.filter(debt => selectedCarrierFilters.includes(debt.carrier_id));
    }

    // Filter by status (resolved/unresolved)
    if (selectedStatusFilters.length > 0) {
      filtered = filtered.filter(debt => {
        if (selectedStatusFilters.includes('resolved') && debt.isResolved) return true;
        if (selectedStatusFilters.includes('unresolved') && !debt.isResolved) return true;
        return false;
      });
    }

    // Filter by date range (created_at - statement date)
    if (dateRange.from || dateRange.to) {
      filtered = filtered.filter(debt => {
        if (!debt.created_at) return false;
        const debtDate = new Date(debt.created_at);
        
        // Set time to start of day for comparison
        const fromDate = dateRange.from ? new Date(dateRange.from.setHours(0, 0, 0, 0)) : null;
        const toDate = dateRange.to ? new Date(dateRange.to.setHours(23, 59, 59, 999)) : null;
        
        if (fromDate && debtDate < fromDate) return false;
        if (toDate && debtDate > toDate) return false;
        return true;
      });
    }

    // Search filter
    if (searchQuery) {
      const searchLower = searchQuery.toLowerCase();
      filtered = filtered.filter(debt =>
        debt.agentOnDebt_name?.toLowerCase().includes(searchLower) ||
        debt.carrier_name?.toLowerCase().includes(searchLower) ||
        debt.agentOnDebt_npn?.toString().includes(searchLower) ||
        debt.amount?.toString().includes(searchLower)
      );
    }

    return filtered;
  }, [debts, searchQuery, selectedAgentId, selectedCarrierFilters, selectedStatusFilters, dateRange]);

  // Filter agents
  const filteredAgents = useMemo(() => {
    let filtered = [...agentSummaries];

    // Agent search filter
    if (agentSearchQuery) {
      const searchLower = agentSearchQuery.toLowerCase();
      filtered = filtered.filter(agent =>
        agent.name?.toLowerCase().includes(searchLower) ||
        agent.npn?.toString().includes(searchLower)
      );
    }

    // Agent multi-select filter
    if (selectedAgentFilters.length > 0) {
      filtered = filtered.filter(agent => selectedAgentFilters.includes(agent.id));
    }

    // Main search filter (for backward compatibility)
    if (searchQuery) {
      const searchLower = searchQuery.toLowerCase();
      filtered = filtered.filter(agent =>
        agent.name?.toLowerCase().includes(searchLower) ||
        agent.npn?.toString().includes(searchLower)
      );
    }

    return filtered;
  }, [agentSummaries, agentSearchQuery, selectedAgentFilters, searchQuery]);

  // Calculate dashboard stats based on filtered debts
  const dashboardStats = useMemo(() => {
    const totalDebt = filteredDebts.reduce((sum, debt) => sum + (debt.amount || 0), 0);
    const uniqueAgents = new Set(filteredDebts.map(d => d.agentOnDebt_id)).size;
    const uniqueCarriers = new Set(filteredDebts.map(d => d.carrier_name).filter(Boolean)).size;
    
    const resolvedDebts = filteredDebts.filter(d => d.isResolved).length;
    const resolvedAmount = filteredDebts.filter(d => d.isResolved).reduce((sum, debt) => sum + (debt.amount || 0), 0);
    const unresolvedDebts = filteredDebts.filter(d => !d.isResolved).length;
    const unresolvedAmount = filteredDebts.filter(d => !d.isResolved).reduce((sum, debt) => sum + (debt.amount || 0), 0);
    
    return {
      totalDebt,
      totalDebts: filteredDebts.length,
      uniqueAgents,
      uniqueCarriers,
      resolvedDebts,
      resolvedAmount,
      unresolvedDebts,
      unresolvedAmount
    };
  }, [filteredDebts]);

  // Pagination
  const totalPages = Math.ceil(filteredDebts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentDebts = filteredDebts.slice(startIndex, endIndex);

  // Format date
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = new Date(timestamp);
      return format(date, 'MMM dd, yyyy');
    } catch {
      return 'N/A';
    }
  };

  // Handle agent click to filter debts
  const handleAgentClick = (agentId) => {
    setSelectedAgentId(agentId === selectedAgentId ? null : agentId);
    setCurrentPage(1); // Reset to first page when filtering
  };

  // Handle edit debt
  const handleEditDebt = async (debt) => {
    setEditingDebt(debt);
    setEditFormData({
      carrier_id: debt.carrier_id,
      amount: debt.amount.toString(),
      isResolved: debt.isResolved || false
    });
    setEditCarrierSearch('');
    setShowEditDebtModal(true);
    
    // Fetch carriers if not already loaded
    if (!carriersCached || carriers.length === 0) {
      await fetchCarriers();
    }
  };

  // Handle update debt - show confirmation
  const handleUpdateDebt = () => {
    if (!editFormData.carrier_id || !editFormData.amount) {
      toast.error('Please fill in all required fields');
      return;
    }
    setShowEditConfirmModal(true);
  };

  // Confirm and execute update
  const handleConfirmUpdate = async () => {
    try {
      setSaving(true);
      
      const updatePayload = {
        debt_id: editingDebt.id,
        user_id: userInfo.user_id,
        debtRecord: {
          amount: parseFloat(editFormData.amount),
          carrier_id: editFormData.carrier_id,
          isResolved: editFormData.isResolved
        },
        location_id: userInfo.location_id
      };
      
      await axios.post(
        'https://api1.simplyworkcrm.com/api:JBYRWqH3/portal/internal/debtUpdate',
        updatePayload
      );
      
      toast.success('Debt record updated successfully');
      setShowEditConfirmModal(false);
      setShowEditDebtModal(false);
      fetchDebts(); // Refresh the list
    } catch (err) {
      console.error('Error updating debt:', err);
      toast.error(err.response?.data?.message || 'Failed to update debt record');
    } finally {
      setSaving(false);
    }
  };

  // Handle delete debt - show confirmation
  const handleDeleteDebt = (debt) => {
    setDeletingDebt(debt);
    setShowDeleteConfirmModal(true);
  };

  // Confirm and execute delete
  const handleConfirmDelete = async () => {
    try {
      setSaving(true);
      
      const deletePayload = {
        debt_id: deletingDebt.id
      };
      
      await axios.post(
        'https://api1.simplyworkcrm.com/api:JBYRWqH3/portal/internal/debtDelete',
        deletePayload,
        {
          params: {
            user_id: userInfo.user_id,
            location_id: userInfo.location_id
          }
        }
      );
      
      toast.success('Debt record deleted successfully');
      setShowDeleteConfirmModal(false);
      fetchDebts(); // Refresh the list
    } catch (err) {
      console.error('Error deleting debt:', err);
      toast.error(err.response?.data?.message || 'Failed to delete debt record');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#d4af37]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <AlertCircle className="w-16 h-16 text-neutral-300" />
        <p className="text-neutral-500 text-lg">{error}</p>
        <Button onClick={fetchDebts} className="bg-[#d4af37] hover:bg-[#b8941e]">
          <RefreshCw className="w-4 h-4 mr-2" />
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="border-0 shadow-sm rounded-xl">
        <CardHeader>
          {/* Title and Refresh */}
          <div className="flex items-center justify-between mb-6">
            <CardTitle className="text-2xl font-bold text-neutral-800">Debt Monitoring</CardTitle>
            <Button
              onClick={fetchDebts}
              variant="outline"
              size="sm"
              className="rounded-lg"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>

          {/* Dashboard Stats */}
          <div className="grid grid-cols-5 gap-4">
        {/* Total Debts */}
        <div className="relative overflow-hidden bg-gradient-to-br from-[#d4af37] via-[#f4e5b8] to-[#fffef0] p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-[#d4af37]/20">
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-neutral-700 uppercase tracking-wider">Total Debts</p>
              <TrendingDown className="w-5 h-5 text-[#b8941e]" />
            </div>
            <p className="text-4xl font-black text-[#b8941e] mb-1">{dashboardStats.totalDebts}</p>
            <p className="text-xs text-neutral-600 font-medium">Debt Records</p>
          </div>
        </div>

        {/* Resolved */}
        <div className="relative overflow-hidden bg-gradient-to-br from-green-400 via-green-300 to-green-100 p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300">
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-green-900 uppercase tracking-wider">Resolved</p>
              <CheckCircle2 className="w-5 h-5 text-green-700" />
            </div>
            <p className="text-4xl font-black text-green-900 mb-1">{dashboardStats.resolvedDebts}</p>
            <p className="text-xs text-green-700 font-medium">
              ${dashboardStats.resolvedAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          </div>
        </div>

        {/* Unresolved */}
        <div className="relative overflow-hidden bg-gradient-to-br from-amber-400 via-amber-300 to-amber-100 p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300">
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-amber-900 uppercase tracking-wider">Unresolved</p>
              <XCircle className="w-5 h-5 text-amber-700" />
            </div>
            <p className="text-4xl font-black text-amber-900 mb-1">{dashboardStats.unresolvedDebts}</p>
            <p className="text-xs text-amber-700 font-medium">
              ${dashboardStats.unresolvedAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          </div>
        </div>

        {/* Unique Agents */}
        <div className="relative overflow-hidden bg-gradient-to-br from-orange-400 via-orange-300 to-orange-100 p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300">
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-orange-900 uppercase tracking-wider">Agents</p>
              <Users className="w-5 h-5 text-orange-700" />
            </div>
            <p className="text-4xl font-black text-orange-900 mb-1">{dashboardStats.uniqueAgents}</p>
            <p className="text-xs text-orange-700 font-medium">With Debt</p>
          </div>
        </div>

        {/* Unique Carriers */}
        <div className="relative overflow-hidden bg-gradient-to-br from-purple-400 via-purple-300 to-purple-100 p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300">
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-purple-900 uppercase tracking-wider">Carriers</p>
              <AlertCircle className="w-5 h-5 text-purple-700" />
            </div>
            <p className="text-4xl font-black text-purple-900 mb-1">{dashboardStats.uniqueCarriers}</p>
            <p className="text-xs text-purple-700 font-medium">Involved</p>
          </div>
        </div>
          </div>
        </CardHeader>

        <CardContent className="pt-6">
          {/* Two Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-10 gap-4">
        {/* Left Column: Agent Summary Table (60%) */}
        <div className="lg:col-span-6">
          <Card className="rounded-2xl">
            <CardContent className="pt-6">
              <div className="space-y-3">
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-semibold text-neutral-700 flex items-center gap-2">
                      <Users className="w-5 h-5 text-[#d4af37]" />
                      Agents Summary
                    </h3>
                    <Badge className="bg-[#d4af37] text-white">
                      {filteredAgents.length}
                    </Badge>
                  </div>
                  <Button
                    onClick={handleOpenCreateModal}
                    size="sm"
                    className="bg-[#d4af37] hover:bg-[#b8941e] text-white rounded-xl"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Report
                  </Button>
                </div>

                {/* Filters */}
                <div className="flex gap-3 mb-3">
                  {/* Quick Search - 50vw */}
                  <div style={{ width: '50vw' }}>
                    <label className="text-xs text-neutral-500 font-medium mb-1 block">Quick Search</label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400 w-4 h-4" />
                      <Input
                        type="text"
                        placeholder="Search by agent name or NPN..."
                        value={agentSearchQuery}
                        onChange={(e) => setAgentSearchQuery(e.target.value)}
                        className="pl-9 rounded-xl"
                      />
                    </div>
                  </div>

                  {/* Filter by Agent */}
                  <div className="flex-1">
                    <label className="text-xs text-neutral-500 font-medium mb-1 block">Filter by Agent</label>
                    <Popover open={agentFilterOpen} onOpenChange={setAgentFilterOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={agentFilterOpen}
                          className="w-full justify-between rounded-xl"
                        >
                          {selectedAgentFilters.length === 0
                            ? "All Agents"
                            : `${selectedAgentFilters.length} selected`}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-full p-0">
                        <div className="flex flex-col">
                    <div className="flex items-center border-b px-3">
                      <svg className="mr-2 h-4 w-4 shrink-0 opacity-50" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <input
                        className="flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-neutral-500"
                        placeholder="Search agent..."
                      />
                    </div>
                    <div className="max-h-64 overflow-auto">
                            {agentSummaries.map((agent) => (
                              <div className="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-2 text-sm outline-none hover:bg-neutral-100 active:bg-neutral-200"
                                key={agent.id}
                                value={agent.name}
                                onClick={() => {
                                  setSelectedAgentFilters(prev =>
                                    prev.includes(agent.id)
                                      ? prev.filter(id => id !== agent.id)
                                      : [...prev, agent.id]
                                  );
                                }}
                              >
                                <Checkbox
                                  checked={selectedAgentFilters.includes(agent.id)}
                                  className="mr-2"
                                />
                                {agent.name}
                              </div>
                            ))}
                          </div>
                  </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                {/* Agents Table */}
                <div className="overflow-x-auto">
                  <div className="max-h-[600px] overflow-y-auto rounded-xl border border-neutral-100">
                    {filteredAgents.length === 0 ? (
                      <div className="text-center py-12">
                        <AlertCircle className="w-12 h-12 mx-auto mb-3 text-neutral-300" />
                        <p className="text-neutral-600">No agents found</p>
                      </div>
                    ) : (
                      <table className="w-full">
                        <thead className="bg-gradient-to-r from-neutral-50 to-neutral-100 sticky top-0 z-10">
                          <tr>
                            <th className="text-left text-xs font-semibold text-neutral-700 px-4 py-3.5 first:rounded-tl-xl">Agent Name</th>
                            <th className="text-left text-xs font-semibold text-neutral-700 px-4 py-3.5">NPN</th>
                            <th className="text-left text-xs font-semibold text-neutral-700 px-4 py-3.5">Email</th>
                            <th className="text-left text-xs font-semibold text-neutral-700 px-4 py-3.5">Resolved</th>
                            <th className="text-left text-xs font-semibold text-neutral-700 px-4 py-3.5 last:rounded-tr-xl">Unresolved</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-neutral-100 bg-white">
                          {filteredAgents.map((agent) => (
                            <tr 
                              key={agent.id} 
                              onClick={() => handleAgentClick(agent.id)}
                              className={cn(
                                "hover:bg-neutral-50 transition-all duration-200 cursor-pointer group",
                                selectedAgentId === agent.id && "bg-[#fffef0] border-l-4 border-l-[#d4af37]"
                              )}
                            >
                              <td className={cn("px-4 py-4", selectedAgentId === agent.id && "pl-6")}>
                                <div className="flex items-center gap-2">
                                  <p className="font-semibold text-neutral-800">{agent.name}</p>
                                  <Badge variant="outline" className="border-neutral-300 bg-neutral-50 text-neutral-600 px-2 py-0.5 text-xs rounded-full">
                                    {agent.totalDebts} {agent.totalDebts === 1 ? 'debt' : 'debts'}
                                  </Badge>
                                </div>
                              </td>
                              <td className="px-4 py-4">
                                <Badge variant="outline" className="border-[#d4af37] text-[#d4af37] bg-[#fffef0]">
                                  {agent.npn}
                                </Badge>
                              </td>
                              <td className="px-4 py-4">
                                <p className="text-sm text-neutral-600">
                                  {agent.email}
                                </p>
                              </td>
                              <td className="px-4 py-4">
                                <p className="text-base font-semibold text-green-600">
                                  ${agent.resolvedAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </p>
                              </td>
                              <td className="px-4 py-4">
                                <p className="text-base font-semibold text-red-600">
                                  ${agent.unresolvedAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </p>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column: All Debt Records Table (40%) */}
        <div className="lg:col-span-4">
          <Card className="rounded-2xl">
            <CardContent className="pt-6">
              <div className="space-y-3">
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <h3 className="text-lg font-semibold text-neutral-700 flex items-center gap-2">
                      <TrendingDown className="w-5 h-5 text-red-600" />
                      Debt Records
                    </h3>
                    <Badge className="bg-red-600 text-white">
                      {filteredDebts.length}
                    </Badge>
                  </div>
                </div>

                {/* Filters Row */}
                <div className="flex gap-3 mb-3">
                  {/* Filter by Carrier */}
                  <div className="flex-1">
                    <label className="text-xs text-neutral-500 font-medium mb-1 block">Filter by Carrier</label>
                    <Popover open={carrierFilterOpen} onOpenChange={setCarrierFilterOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={carrierFilterOpen}
                          className="w-full justify-between rounded-xl"
                        >
                          {selectedCarrierFilters.length === 0
                            ? "All Carriers"
                            : `${selectedCarrierFilters.length} selected`}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-full p-0">
                        <div className="flex flex-col">
                    <div className="flex items-center border-b px-3">
                      <svg className="mr-2 h-4 w-4 shrink-0 opacity-50" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <input
                        className="flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-neutral-500"
                        placeholder="Search carrier..."
                      />
                    </div>
                    <div className="max-h-64 overflow-auto">
                            {uniqueCarriers.map((carrier) => (
                              <div className="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-2 text-sm outline-none hover:bg-neutral-100 active:bg-neutral-200"
                                key={carrier.id}
                                value={carrier.name}
                                onClick={() => {
                                  setSelectedCarrierFilters(prev =>
                                    prev.includes(carrier.id)
                                      ? prev.filter(id => id !== carrier.id)
                                      : [...prev, carrier.id]
                                  );
                                }}
                              >
                                <Checkbox
                                  checked={selectedCarrierFilters.includes(carrier.id)}
                                  className="mr-2"
                                />
                                {carrier.name}
                              </div>
                            ))}
                          </div>
                  </div>
                      </PopoverContent>
                    </Popover>
                  </div>

                  {/* Filter by Status */}
                  <div className="flex-1">
                    <label className="text-xs text-neutral-500 font-medium mb-1 block">Filter by Status</label>
                    <Popover open={statusFilterOpen} onOpenChange={setStatusFilterOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={statusFilterOpen}
                          className="w-full justify-between rounded-xl"
                        >
                          {selectedStatusFilters.length === 0
                            ? "All Status"
                            : `${selectedStatusFilters.length} selected`}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-full p-0">
                        <div className="flex flex-col p-1">
                            <div className="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-2 text-sm outline-none hover:bg-neutral-100 active:bg-neutral-200"
                              value="resolved"
                              onClick={() => {
                                setSelectedStatusFilters(prev =>
                                  prev.includes('resolved')
                                    ? prev.filter(s => s !== 'resolved')
                                    : [...prev, 'resolved']
                                );
                              }}
                            >
                              <Checkbox
                                checked={selectedStatusFilters.includes('resolved')}
                                className="mr-2"
                              />
                              Resolved
                            </div>
                            <div className="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-2 text-sm outline-none hover:bg-neutral-100 active:bg-neutral-200"
                              value="unresolved"
                              onClick={() => {
                                setSelectedStatusFilters(prev =>
                                  prev.includes('unresolved')
                                    ? prev.filter(s => s !== 'unresolved')
                                    : [...prev, 'unresolved']
                                );
                              }}
                            >
                              <Checkbox
                                checked={selectedStatusFilters.includes('unresolved')}
                                className="mr-2"
                              />
                              Unresolved
                            </div>
                          </div>
                      </PopoverContent>
                    </Popover>
                  </div>

                  {/* Date Range Filter */}
                  <div className="flex-1">
                    <label className="text-xs text-neutral-500 font-medium mb-1 block">Statement Date Range</label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal rounded-xl",
                            !dateRange.from && "text-muted-foreground"
                          )}
                        >
                          <Calendar className="mr-2 h-4 w-4" />
                          {dateRange.from ? (
                            dateRange.to ? (
                              <>
                                {format(dateRange.from, "MMM dd, yyyy")} -{" "}
                                {format(dateRange.to, "MMM dd, yyyy")}
                              </>
                            ) : (
                              format(dateRange.from, "MMM dd, yyyy")
                            )
                          ) : (
                            <span>Pick a date range</span>
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <CalendarComponent
                          initialFocus
                          mode="range"
                          defaultMonth={dateRange.from}
                          selected={{ from: dateRange.from, to: dateRange.to }}
                          onClick={(range) => setDateRange({ from: range?.from, to: range?.to })}
                          numberOfMonths={2}
                        />
                        {(dateRange.from || dateRange.to) && (
                          <div className="p-3 border-t">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setDateRange({ from: null, to: null })}
                              className="w-full"
                            >
                              Clear Date Range
                            </Button>
                          </div>
                        )}
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                {/* Clear Filters */}
                {(selectedAgentId || selectedCarrierFilters.length > 0 || selectedStatusFilters.length > 0 || dateRange.from || dateRange.to) && (
                  <div className="flex items-center gap-2 mb-3">
                    {selectedAgentId && <Badge variant="outline" className="text-xs">Filtered by agent</Badge>}
                    {selectedCarrierFilters.length > 0 && <Badge variant="outline" className="text-xs">{selectedCarrierFilters.length} carrier(s)</Badge>}
                    {selectedStatusFilters.length > 0 && <Badge variant="outline" className="text-xs">{selectedStatusFilters.length} status</Badge>}
                    {(dateRange.from || dateRange.to) && <Badge variant="outline" className="text-xs">Date range</Badge>}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setSelectedAgentId(null);
                        setSelectedCarrierFilters([]);
                        setSelectedStatusFilters([]);
                        setDateRange({ from: null, to: null });
                      }}
                      className="h-6 text-xs"
                    >
                      Clear All
                    </Button>
                  </div>
                )}

                {/* All Debt Records Table */}
                {/* Showing Info & Page Size Selector */}
                <div className="flex items-center justify-between text-sm text-neutral-600 mb-2">
                  <div className="flex items-center gap-3">
                    <span>
                      Showing {filteredDebts.length === 0 ? 0 : startIndex + 1} - {Math.min(endIndex, filteredDebts.length)} of {filteredDebts.length}
                    </span>
                  </div>
                  {totalPages > 1 && <span className="text-xs">Page {currentPage} of {totalPages}</span>}
                </div>

                {/* Debt Cards */}
                {filteredDebts.length === 0 ? (
                  <div className="text-center py-12 text-neutral-400">
                    <TrendingDown className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p className="text-sm">{selectedAgentId ? 'No debts for selected agent' : 'No debt records found'}</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[600px] overflow-y-auto">
                    {currentDebts.map((debt) => (
                      <div 
                        key={debt.id} 
                        className="bg-white p-3 rounded-xl border-2 border-neutral-200 hover:border-red-400 transition-all"
                      >
                        <div className="space-y-3">
                          {/* Header: Amount and Status Badge */}
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="text-lg font-bold text-red-600">
                                ${parseFloat(debt.amount || 0).toFixed(2)}
                              </p>
                              <p className="text-xs text-neutral-500">{formatDate(debt.created_at)}</p>
                            </div>
                            {debt.isResolved ? (
                              <Badge className="bg-green-500 text-white text-xs">
                                Resolved
                              </Badge>
                            ) : (
                              <Badge className="bg-red-500 text-white text-xs">
                                Unresolved
                              </Badge>
                            )}
                          </div>

                          {/* Carrier - Highlighted */}
                          <div className="bg-[#fffef0] border border-[#d4af37] rounded-lg p-2">
                            <p className="text-xs text-neutral-500 mb-0.5">Carrier</p>
                            <p className="text-sm font-bold text-[#d4af37]">
                              {debt.carrier_name || 'N/A'}
                            </p>
                          </div>

                          {/* Agent Info */}
                          <div>
                            <p className="text-xs text-neutral-500">Agent on Debt</p>
                            <p className="text-sm font-semibold text-neutral-800">
                              {debt.agentOnDebt_name || 'N/A'}
                            </p>
                            <p className="text-xs text-neutral-400">
                              NPN: {debt.agentOnDebt_npn || 'N/A'}
                            </p>
                          </div>

                          {/* Submitted By */}
                          {debt.submittedBy_name && (
                            <div>
                              <p className="text-xs text-neutral-500">Submitted By</p>
                              <p className="text-xs font-semibold text-neutral-800">
                                {debt.submittedBy_name}
                              </p>
                            </div>
                          )}

                          {/* Action Buttons */}
                          <div className="pt-2 border-t border-neutral-200 flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditDebt(debt);
                              }}
                              className="flex-1 rounded-xl text-xs"
                            >
                              <Edit2 className="w-3 h-3 mr-1" />
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteDebt(debt);
                              }}
                              className="rounded-xl text-xs border-red-300 text-red-600 hover:bg-red-50"
                            >
                              <Trash2 className="w-3 h-3 mr-1" />
                              Delete
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-neutral-200">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="rounded-xl"
                    >
                      Previous
                    </Button>
                    <span className="text-sm text-neutral-600">
                      Page {currentPage} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="rounded-xl"
                    >
                      Next
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
        </CardContent>
      </Card>

      {/* Create Report Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>Create Debt Report</DialogTitle>
            <DialogDescription>
              Add debt entries for an agent across multiple carriers
            </DialogDescription>
          </DialogHeader>

          {/* Scrollable Content */}
          <div className="overflow-y-auto flex-1 pr-2">
            <div className="space-y-4 py-4">
              {/* Agent on Debt Section */}
              <div className="bg-[#fffef0] p-4 rounded-xl border border-[#d4af37]">
                <h3 className="text-sm font-semibold text-neutral-700 mb-3">Agent on Debt</h3>
                
                {npnVerification.status === 'success' ? (
                  <div>
                    <p className="text-base font-semibold text-neutral-800">
                      {npnVerification.data.first_name} {npnVerification.data.last_name}
                    </p>
                    <p className="text-sm text-neutral-500">NPN: {formData.npn}</p>
                    {agentEmail && (
                      <p className="text-sm text-neutral-500">Email: {agentEmail}</p>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label htmlFor="npn" className="text-neutral-600 text-xs">
                      Enter Agent NPN to verify
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        id="npn"
                        type="text"
                        placeholder="Enter Agent NPN"
                        value={formData.npn}
                        onChange={(e) => {
                          setFormData({ ...formData, npn: e.target.value });
                          setNpnVerification({ status: '', data: null });
                        }}
                        className="flex-1"
                        required
                      />
                      <Button
                        type="button"
                        onClick={() => verifyNPN(formData.npn)}
                        disabled={!formData.npn || verifyingNpn}
                        variant="outline"
                      >
                        {verifyingNpn ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Verifying...
                          </>
                        ) : (
                          'Verify'
                        )}
                      </Button>
                    </div>
                    
                    {npnVerification.status === 'inactive' && (
                      <p className="text-xs text-orange-600">NPN status is not active</p>
                    )}
                    {npnVerification.status === 'error' && (
                      <p className="text-xs text-red-600">Failed to verify NPN. Please check the number.</p>
                    )}
                  </div>
                )}
              </div>

              {/* Add Debt Entry Form */}
              {npnVerification.status === 'success' && (
                <>
                  <form onSubmit={handleSubmit} id="create-debt-form">
                    <div className="space-y-3">

                      {/* Carrier */}
                      <div>
                        <Label htmlFor="carrier" className="text-neutral-600 text-xs mb-1.5 block">
                          Carrier
                        </Label>
                        {loadingCarriers ? (
                          <div className="flex items-center justify-center py-4">
                            <Loader2 className="w-5 h-5 animate-spin text-[#d4af37]" />
                          </div>
                        ) : (
                          <Select
                            value={formData.carrier_id}
                            onValueChange={(value) => setFormData({ ...formData, carrier_id: value })}
                            required
                          >
                            <SelectTrigger id="carrier">
                              <SelectValue placeholder="Select carrier" />
                            </SelectTrigger>
                            <SelectContent className="max-h-[300px]">
                              <div className="sticky top-0 z-10 bg-white p-2 border-b">
                                <div className="relative">
                                  <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-neutral-400 w-4 h-4" />
                                  <Input
                                    type="text"
                                    placeholder="Search carriers..."
                                    value={carrierSearch}
                                    onChange={(e) => setCarrierSearch(e.target.value)}
                                    className="pl-8 h-8"
                                    onClick={(e) => e.stopPropagation()}
                                    onKeyDown={(e) => e.stopPropagation()}
                                  />
                                </div>
                              </div>
                              {carriers
                                .filter(carrier => 
                                  !carrierSearch || 
                                  carrier.label.toLowerCase().includes(carrierSearch.toLowerCase())
                                )
                                .map(carrier => (
                                  <SelectItem key={carrier.value} value={carrier.value}>
                                    {carrier.label}
                                  </SelectItem>
                                ))
                              }
                              {carriers.filter(carrier => 
                                !carrierSearch || 
                                carrier.label.toLowerCase().includes(carrierSearch.toLowerCase())
                              ).length === 0 && carrierSearch && (
                                <div className="py-6 text-center text-sm text-neutral-500">
                                  No carriers found
                                </div>
                              )}
                            </SelectContent>
                          </Select>
                        )}
                      </div>

                      {/* Amount */}
                      <div>
                        <Label htmlFor="amount" className="text-neutral-600 text-xs mb-1.5 block">
                          Debt Amount
                        </Label>
                        <Input
                          id="amount"
                          type="number"
                          step="0.01"
                          min="0.01"
                          placeholder="0.00"
                          value={formData.amount}
                          onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                          required
                        />
                      </div>
                    </div>
                  </form>

                  {/* Debt Entries List */}
                  {reportsAdded.length > 0 && (
                    <div className="mt-4">
                      <h3 className="text-sm font-semibold text-neutral-700 mb-2">
                        Debt Entries ({reportsAdded.length})
                      </h3>
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {reportsAdded.map((report, index) => (
                          <div key={index} className="bg-white p-3 rounded-lg border border-neutral-200 hover:border-[#d4af37] transition-all">
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <p className="font-medium text-neutral-800">{report.carrier_name}</p>
                                <p className="text-xs text-neutral-500">Entry #{index + 1}</p>
                              </div>
                              <div className="flex items-center gap-3">
                                <p className="text-lg font-semibold text-[#d4af37]">
                                  ${report.amount.toFixed(2)}
                                </p>
                                <div className="flex items-center gap-1">
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleEditEntry(index)}
                                    className="h-8 w-8 p-0 hover:bg-blue-50 hover:text-blue-600"
                                    title="Edit entry"
                                  >
                                    <Edit2 className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleDeleteEntry(index)}
                                    className="h-8 w-8 p-0 hover:bg-red-50 hover:text-red-600"
                                    title="Delete entry"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Add Another Button */}
                  {reportsAdded.length > 0 && (
                    <div className="flex justify-center mt-4">
                      <Button
                        type="submit"
                        form="create-debt-form"
                        variant="outline"
                        disabled={npnVerification.status !== 'success' || submitting}
                        className="border-[#d4af37] text-[#d4af37] hover:bg-[#fffef0]"
                      >
                        {submitting ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            {isEditMode ? 'Updating...' : 'Adding...'}
                          </>
                        ) : isEditMode ? (
                          <>
                            <CheckCircle2 className="w-4 h-4 mr-2" />
                            Update Entry
                          </>
                        ) : (
                          <>
                            <Plus className="w-4 h-4 mr-2" />
                            Add Another Entry
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Footer - Fixed at bottom */}
          <DialogFooter className="flex-shrink-0 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowCreateModal(false)}
            >
              Cancel
            </Button>
            {reportsAdded.length === 0 ? (
              <Button
                type="submit"
                form="create-debt-form"
                disabled={npnVerification.status !== 'success' || submitting}
                className="bg-[#d4af37] hover:bg-[#b8941e]"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {isEditMode ? 'Updating...' : 'Adding...'}
                  </>
                ) : isEditMode ? (
                  'Update Entry'
                ) : (
                  'Add Entry'
                )}
              </Button>
            ) : (
              <Button
                type="button"
                onClick={handleSaveClick}
                className="bg-[#d4af37] hover:bg-[#b8941e]"
              >
                Save {reportsAdded.length} {reportsAdded.length === 1 ? 'Entry' : 'Entries'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation Modal */}
      <Dialog open={showConfirmModal} onOpenChange={setShowConfirmModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Debt Reports</DialogTitle>
            <DialogDescription>
              Are you sure you want to save these debt entries?
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Agent Summary */}
            <div className="bg-[#fffef0] p-3 rounded-lg border border-[#d4af37]">
              <p className="text-xs text-neutral-500 mb-1">Agent on Debt</p>
              <p className="font-semibold text-neutral-800">
                {npnVerification.data?.first_name} {npnVerification.data?.last_name}
              </p>
              <p className="text-sm text-neutral-600">NPN: {formData.npn}</p>
            </div>

            {/* Entries Summary */}
            <div>
              <p className="text-sm font-semibold text-neutral-700 mb-2">
                Debt Entries ({reportsAdded.length})
              </p>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {reportsAdded.map((report, index) => (
                  <div key={index} className="flex items-center justify-between bg-neutral-50 p-3 rounded-lg border border-neutral-200">
                    <div>
                      <p className="font-medium text-neutral-800">{report.carrier_name}</p>
                      <p className="text-xs text-neutral-500">Entry #{index + 1}</p>
                    </div>
                    <p className="text-lg font-semibold text-[#d4af37]">
                      ${report.amount.toFixed(2)}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Total Summary */}
            <div className="pt-3 border-t">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-neutral-700">Total Debt Amount:</p>
                <p className="text-xl font-bold text-red-600">
                  ${reportsAdded.reduce((sum, r) => sum + r.amount, 0).toFixed(2)}
                </p>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowConfirmModal(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleConfirmSave}
              disabled={saving}
              className="bg-[#d4af37] hover:bg-[#b8941e]"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Confirm & Save'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Debt Modal */}
      <Dialog open={showEditDebtModal} onOpenChange={setShowEditDebtModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>Edit Debt Record</DialogTitle>
            <DialogDescription>
              Update debt information for this record
            </DialogDescription>
          </DialogHeader>

          {editingDebt && (
            <div className="overflow-y-auto flex-1 pr-2">
              <div className="space-y-4 py-4">
                {/* Current Data Header */}
                <div className="bg-neutral-50 p-4 rounded-xl border border-neutral-200">
                  <h3 className="text-sm font-semibold text-neutral-700 mb-3">Current Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-neutral-500">Agent on Debt</p>
                      <p className="text-sm font-semibold text-neutral-800">
                        {editingDebt.agentOnDebt_name || 'N/A'}
                      </p>
                      <p className="text-xs text-neutral-500">NPN: {editingDebt.agentOnDebt_npn || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-neutral-500">Current Carrier</p>
                      <p className="text-sm font-semibold text-[#d4af37]">
                        {editingDebt.carrier_name || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-neutral-500">Current Amount</p>
                      <p className="text-lg font-bold text-red-600">
                        ${parseFloat(editingDebt.amount || 0).toFixed(2)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-neutral-500">Status</p>
                      {editingDebt.isResolved ? (
                        <Badge className="bg-green-500 text-white text-xs">
                          Resolved
                        </Badge>
                      ) : (
                        <Badge className="bg-red-500 text-white text-xs">
                          Unresolved
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>

                {/* Update Form */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-neutral-700">Update Information</h3>
                  
                  {/* Carrier */}
                  <div>
                    <Label htmlFor="edit-carrier" className="text-neutral-600 text-xs mb-1.5 block">
                      Carrier <span className="text-red-500">*</span>
                    </Label>
                    {loadingCarriers ? (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 className="w-5 h-5 animate-spin text-[#d4af37]" />
                      </div>
                    ) : (
                      <Select
                        value={editFormData.carrier_id}
                        onValueChange={(value) => setEditFormData({ ...editFormData, carrier_id: value })}
                        required
                      >
                        <SelectTrigger id="edit-carrier">
                          <SelectValue placeholder="Select carrier" />
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                          <div className="sticky top-0 z-10 bg-white p-2 border-b">
                            <div className="relative">
                              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-neutral-400 w-4 h-4" />
                              <Input
                                type="text"
                                placeholder="Search carriers..."
                                value={editCarrierSearch}
                                onChange={(e) => setEditCarrierSearch(e.target.value)}
                                className="pl-8 h-8"
                                onClick={(e) => e.stopPropagation()}
                                onKeyDown={(e) => e.stopPropagation()}
                              />
                            </div>
                          </div>
                          {carriers
                            .filter(carrier => 
                              !editCarrierSearch || 
                              carrier.label.toLowerCase().includes(editCarrierSearch.toLowerCase())
                            )
                            .map(carrier => (
                              <SelectItem key={carrier.value} value={carrier.value}>
                                {carrier.label}
                              </SelectItem>
                            ))
                          }
                          {carriers.filter(carrier => 
                            !editCarrierSearch || 
                            carrier.label.toLowerCase().includes(editCarrierSearch.toLowerCase())
                          ).length === 0 && editCarrierSearch && (
                            <div className="py-6 text-center text-sm text-neutral-500">
                              No carriers found
                            </div>
                          )}
                        </SelectContent>
                      </Select>
                    )}
                  </div>

                  {/* Amount */}
                  <div>
                    <Label htmlFor="edit-amount" className="text-neutral-600 text-xs mb-1.5 block">
                      Debt Amount <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="edit-amount"
                      type="number"
                      step="0.01"
                      min="0.01"
                      placeholder="0.00"
                      value={editFormData.amount}
                      onChange={(e) => setEditFormData({ ...editFormData, amount: e.target.value })}
                      required
                    />
                  </div>

                  {/* Is Resolved Toggle */}
                  <div className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg border border-neutral-200">
                    <div className="flex-1">
                      <Label htmlFor="edit-resolved" className="text-sm font-medium text-neutral-700 cursor-pointer">
                        Mark as Resolved
                      </Label>
                      <p className="text-xs text-neutral-500 mt-0.5">
                        Toggle to mark this debt as resolved or unresolved
                      </p>
                    </div>
                    <Switch
                      id="edit-resolved"
                      checked={editFormData.isResolved}
                      onCheckedChange={(checked) => setEditFormData({ ...editFormData, isResolved: checked })}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Footer */}
          <DialogFooter className="flex-shrink-0 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowEditDebtModal(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleUpdateDebt}
              disabled={saving}
              className="bg-[#d4af37] hover:bg-[#b8941e]"
            >
              Update Debt
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Confirmation Modal */}
      <Dialog open={showEditConfirmModal} onOpenChange={setShowEditConfirmModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Update</DialogTitle>
            <DialogDescription>
              Are you sure you want to update this debt record?
            </DialogDescription>
          </DialogHeader>

          {editingDebt && (
            <div className="space-y-3 py-4">
              {/* Current vs New Comparison */}
              <div className="bg-neutral-50 p-3 rounded-lg border border-neutral-200">
                <p className="text-xs font-semibold text-neutral-700 mb-2">Changes Summary</p>
                
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-neutral-600">Carrier:</span>
                    <span className="font-medium text-neutral-800">
                      {carriers.find(c => c.value === editFormData.carrier_id)?.label || 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-600">Amount:</span>
                    <span className="font-semibold text-red-600">
                      ${parseFloat(editFormData.amount || 0).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-600">Status:</span>
                    {editFormData.isResolved ? (
                      <Badge className="bg-green-500 text-white text-xs">Resolved</Badge>
                    ) : (
                      <Badge className="bg-red-500 text-white text-xs">Unresolved</Badge>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowEditConfirmModal(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleConfirmUpdate}
              disabled={saving}
              className="bg-[#d4af37] hover:bg-[#b8941e]"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Updating...
                </>
              ) : (
                'Confirm Update'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteConfirmModal} onOpenChange={setShowDeleteConfirmModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this debt record? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          {deletingDebt && (
            <div className="space-y-3 py-4">
              <div className="bg-red-50 p-3 rounded-lg border border-red-200">
                <p className="text-xs font-semibold text-red-700 mb-2">Debt Record Details</p>
                
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-neutral-600">Agent:</span>
                    <span className="font-medium text-neutral-800">
                      {deletingDebt.agentOnDebt_name || 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-600">Carrier:</span>
                    <span className="font-medium text-neutral-800">
                      {deletingDebt.carrier_name || 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-600">Amount:</span>
                    <span className="font-semibold text-red-600">
                      ${parseFloat(deletingDebt.amount || 0).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowDeleteConfirmModal(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleConfirmDelete}
              disabled={saving}
              variant="destructive"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Confirm Delete'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Modal */}
      <Dialog open={showEmailModal} onOpenChange={(open) => {
        if (!open) {
          // If modal is closed without saving email, clear NPN verification
          setShowEmailModal(false);
          setAgentEmail('');
          setNpnVerification({ status: '', data: null });
          setFormData({ ...formData, npn: '' });
          toast.warning('Email is required to create debt records. Please verify NPN and provide email.');
        }
      }}>
        <DialogContent className="max-w-md" onPointerDownOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>Agent Email Required</DialogTitle>
            <DialogDescription>
              This agent doesn't have an email on file. Email is required to create debt records. Please provide their email address to continue.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <Label htmlFor="agent-email" className="text-sm font-medium text-neutral-700 mb-2 block">
              Agent Email Address <span className="text-red-500">*</span>
            </Label>
            <Input
              id="agent-email"
              type="email"
              placeholder="agent@example.com"
              value={agentEmail}
              onChange={(e) => setAgentEmail(e.target.value)}
              required
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowEmailModal(false);
                setAgentEmail('');
                setNpnVerification({ status: '', data: null });
                setFormData({ ...formData, npn: '' });
                toast.warning('Email is required to create debt records. Please verify NPN and provide email.');
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleEmailSubmit}
              className="bg-[#d4af37] hover:bg-[#b8941e]"
            >
              Save Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Agent List Panel */}
      <AgentListPanel />
    </div>
  );
}
